import React, { useState, useEffect } from 'react';
import { collection, getDocs, orderBy, query, limit, startAfter } from 'firebase/firestore';
import { db } from './firebase';
import logoImg from './assets/image.png';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCursors, setPageCursors] = useState([null]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const PAGE_SIZE = 10;

  // Fallback State
  const [isFallback, setIsFallback] = useState(false);
  const [allFallbackUsers, setAllFallbackUsers] = useState([]);

  // Export State
  const [isExporting, setIsExporting] = useState(false);

  const fetchUsers = async (page = 0, cursors = pageCursors) => {
    try {
      setLoading(true);

      const constraints = [orderBy("timestamp", "desc"), limit(PAGE_SIZE + 1)];
      if (cursors[page]) {
        constraints.push(startAfter(cursors[page]));
      }

      const q = query(collection(db, "users"), ...constraints);
      const querySnapshot = await getDocs(q);

      const usersData = [];
      querySnapshot.forEach((doc) => {
        usersData.push({ id: doc.id, ...doc.data(), docRef: doc });
      });

      setHasNextPage(usersData.length > PAGE_SIZE);
      const displayUsers = usersData.slice(0, PAGE_SIZE);

      if (usersData.length > PAGE_SIZE) {
        const newCursors = [...cursors];
        newCursors[page + 1] = displayUsers[displayUsers.length - 1].docRef;
        setPageCursors(newCursors);
      }

      setUsers(displayUsers);
      setCurrentPage(page);
      setIsFallback(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      // Fallback to client-side pagination if indexing err
      if (error.message.includes("indexes") || error.message.includes("does not exist")) {
        setIsFallback(true);
        try {
          const fallbackQ = query(collection(db, "users"));
          const fallbackSnap = await getDocs(fallbackQ);
          const usersData = [];
          fallbackSnap.forEach((doc) => {
            usersData.push({ id: doc.id, ...doc.data() });
          });
          usersData.sort((a, b) => {
            const tA = typeof a.timestamp === 'string' ? new Date(a.timestamp).getTime() : a.timestamp?.toMillis ? a.timestamp.toMillis() : 0;
            const tB = typeof b.timestamp === 'string' ? new Date(b.timestamp).getTime() : b.timestamp?.toMillis ? b.timestamp.toMillis() : 0;
            return tB - tA;
          });
          setAllFallbackUsers(usersData);
          setUsers(usersData.slice(0, PAGE_SIZE));
          setHasNextPage(usersData.length > PAGE_SIZE);
          setCurrentPage(0);
        } catch (e) {
          console.error("Fallback failed", e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(0, [null]);
  }, []);

  const handleNextPage = () => {
    if (isFallback) {
      const nextIdx = (currentPage + 1) * PAGE_SIZE;
      setUsers(allFallbackUsers.slice(nextIdx, nextIdx + PAGE_SIZE));
      setCurrentPage(currentPage + 1);
      setHasNextPage(allFallbackUsers.length > nextIdx + PAGE_SIZE);
    } else {
      if (hasNextPage) {
        fetchUsers(currentPage + 1);
      }
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      if (isFallback) {
        const prevIdx = (currentPage - 1) * PAGE_SIZE;
        setUsers(allFallbackUsers.slice(prevIdx, prevIdx + PAGE_SIZE));
        setCurrentPage(currentPage - 1);
        setHasNextPage(true); // Since we went back, we naturally have a next page
      } else {
        fetchUsers(currentPage - 1);
      }
    }
  };

  const exportToExcel = async () => {
    try {
      setIsExporting(true);

      let querySnapshot;
      try {
        const q = query(collection(db, "users"), orderBy("timestamp", "desc"));
        querySnapshot = await getDocs(q);
      } catch (err) {
        if (err.message.includes("indexes") || err.message.includes("does not exist")) {
          const fallbackQ = query(collection(db, "users"));
          querySnapshot = await getDocs(fallbackQ);
        } else {
          throw err;
        }
      }

      let usersData = [];
      querySnapshot.forEach((doc) => {
        usersData.push({ id: doc.id, ...doc.data() });
      });

      // Sort ensuring descending order by timestamp
      usersData.sort((a, b) => {
        const tA = typeof a.timestamp === 'string' ? new Date(a.timestamp).getTime() : a.timestamp?.toMillis ? a.timestamp.toMillis() : 0;
        const tB = typeof b.timestamp === 'string' ? new Date(b.timestamp).getTime() : b.timestamp?.toMillis ? b.timestamp.toMillis() : 0;
        return tB - tA;
      });

      // Escape CSV helper
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '""';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      // Headers
      const headers = ["Customer Name", "Mobile", "Location", "Registered At"];

      // Formatting rows
      const csvRows = [];
      csvRows.push(headers.map(escapeCSV).join(","));

      usersData.forEach(user => {
        const name = user.name || '-';
        const mobile = user.mobile || '-';
        const location = user.location || '-';
        let registeredAt = '-';
        if (user.timestamp) {
          if (typeof user.timestamp === 'string') registeredAt = user.timestamp;
          else if (user.timestamp?.toDate) registeredAt = user.timestamp.toDate().toLocaleString();
        }

        csvRows.push([name, mobile, location, registeredAt].map(escapeCSV).join(","));
      });

      const csvString = csvRows.join("\r\n");
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `customer_data_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error("Error exporting data:", error);
      alert("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === 'Admin' && password === 'Admin123') {
      setIsAuthenticated(true);
      setLoginError('');
      // After login, fetch the initial data explicitly if not already loading.
      // But actually useEffect handles fetching on mount, let's just trigger fetch if users array is empty
      if (users.length === 0) {
        fetchUsers(0, [null]);
      }
    } else {
      setLoginError('Invalid username or password');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="login-container">
        <div className="login-card">
          <img src={logoImg} alt="Logo" style={{ height: '60px', marginBottom: '16px' }} />
          <h2>Admin Portal Login</h2>
          <form className="login-form" onSubmit={handleLogin}>
            <input
              type="text"
              className="login-input"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <input
              type="password"
              className="login-input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {loginError && <div className="login-error">{loginError}</div>}
            <button type="submit" className="login-btn">Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="simple-layout">
      <header className="simple-header">
        <div className="header-brand">
          <img src={logoImg} alt="Logo" className="header-logo" />
          <h1>Admin Portal</h1>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="simple-btn"
            onClick={exportToExcel}
            disabled={isExporting}
            style={{ backgroundColor: '#2e7d32', color: 'white' }}
          >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </button>
          <button className="simple-btn" onClick={() => fetchUsers(0, [null])} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh Data'}
          </button>
        </div>
      </header>

      <main className="table-container">
        {loading && users.length === 0 ? (
          <div className="loader">Fetching data...</div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Mobile</th>
                  <th>Location</th>
                  <th>Registered At</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.name || '-'}</td>
                    <td>{user.mobile || '-'}</td>
                    <td>{user.location || '-'}</td>
                    <td>
                      {user.timestamp
                        ? (typeof user.timestamp === 'string' ? user.timestamp : (user.timestamp?.toDate ? user.timestamp.toDate().toLocaleString() : '-'))
                        : '-'}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="4" className="text-center">No customers found.</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination UI */}
            {(!loading && (hasNextPage || currentPage > 0)) && (
              <div className="pagination-container">
                <button
                  className="pagination-btn"
                  onClick={handlePrevPage}
                  disabled={currentPage === 0 || loading}
                >
                  &larr; Previous
                </button>
                <div className="pagination-info">
                  Page {currentPage + 1} {isFallback && `of ${Math.ceil(allFallbackUsers.length / PAGE_SIZE)}`}
                </div>
                <button
                  className="pagination-btn"
                  onClick={handleNextPage}
                  disabled={!hasNextPage || loading}
                >
                  Next &rarr;
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default App;
